
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation } from '@tanstack/react-query';

interface DocumentGenerationParams {
  template: string;
  documentType: 'certificate' | 'report' | 'regulatory';
  data: Record<string, any>;
  fileName?: string;
}

interface DocumentGenerationResult {
  success: boolean;
  fileUrl?: string;
  documentId?: string;
  message?: string;
  error?: string;
}

export function useDocumentGeneration() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const generateDocumentMutation = useMutation({
    mutationFn: async (params: DocumentGenerationParams): Promise<DocumentGenerationResult> => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Add the current user ID to the document data for tracking
        const currentUser = (await supabase.auth.getUser()).data.user;
        const dataWithUser = {
          ...params.data,
          userId: currentUser?.id
        };
        
        // Call the edge function to generate the document
        const { data, error } = await supabase.functions.invoke('generate-pdf', {
          body: {
            template: params.template,
            documentType: params.documentType,
            data: dataWithUser,
            fileName: params.fileName
          }
        });
        
        if (error) throw error;
        
        toast({
          title: "Document Generated",
          description: `Your ${params.documentType} has been generated successfully.`,
        });
        
        return data as DocumentGenerationResult;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to generate document';
        setError(err);
        toast({
          variant: 'destructive',
          title: "Error",
          description: errorMessage,
        });
        return {
          success: false,
          error: errorMessage
        };
      } finally {
        setIsLoading(false);
      }
    }
  });

  const generateDocument = (params: DocumentGenerationParams, callback?: (result: DocumentGenerationResult) => void) => {
    generateDocumentMutation.mutate(params, {
      onSuccess: (data) => {
        if (callback) callback(data);
      },
      onError: (err) => {
        const errorResult: DocumentGenerationResult = {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error'
        };
        if (callback) callback(errorResult);
      }
    });
  };

  const logDocumentAccess = async (documentId: string, accessType: 'view' | 'download' | 'share' | 'print') => {
    try {
      // Add an entry to the document access logs for audit purposes
      await supabase
        .from('document_access_logs')
        .insert({
          document_id: documentId,
          access_type: accessType,
          accessed_by: (await supabase.auth.getUser()).data.user?.id
        });
        
      return true;
    } catch (error) {
      console.error('Error logging document access:', error);
      return false;
    }
  };

  return {
    generateDocument,
    logDocumentAccess,
    isLoading: generateDocumentMutation.isPending || isLoading,
    error
  };
}
